import React from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";

import { Container, InputAdornment } from "@mui/material";
import { Filter as FilterIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";
import InventoryBox from "components/inventory/InventoryBox";
import AddInventoryEntryForm from "components/inventory/AddInventoryEntryDialog";

import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

const QUERY = gql`
  query {
    inventory {
      id
      box

      ...InventoryBox_inventory
    }
  }

  ${InventoryBox.fragments.inventory}
`;

const SUBSCRIPTION = gql`
  subscription {
    inventoryChangeStream {
      id
      operationType
      inventory {
        id
        box

        ...InventoryBox_inventory
      }
    }
  }

  ${InventoryBox.fragments.inventory}
`;

const makeColumns = (end) => {
  const start = "A".charCodeAt(0);
  const length = end.charCodeAt(0) - start + 1;
  return Array.from({ length }, (_, idx) => String.fromCharCode(start + idx));
};

const makeRows = (end) => {
  const length = parseInt(end, 10) + 1;
  return Array.from({ length }, (_, idx) => idx.toString()).splice(1);
};

const makeBox = (id, instrument, instrumentType, endColumn, endRow) => ({
  id,
  instrument,
  instrumentType,
  columns: makeColumns(endColumn),
  rows: makeRows(endRow),
  entries: [],
});

// prettier-ignore
const makeBoxes = () => ({
  A1:  makeBox("A1",  "saxophone", "alto",     "J", "7"),
  A2:  makeBox("A2",  "saxophone", "alto",     "K", "7"),
  T1:  makeBox("T1",  "saxophone", "tenor",    "J", "5"),
  T2:  makeBox("T2",  "saxophone", "tenor",    "J", "6"),
  T3:  makeBox("T3",  "saxophone", "tenor",    "J", "6"),
  T4:  makeBox("T4",  "saxophone", "tenor",    "J", "6"),
  B1:  makeBox("B1",  "saxophone", "baritone", "J", "6"),
  VS1: makeBox("VS1", "saxophone", "soprano",  "J", "6"),
  VC1: makeBox("VC1", "clarinet",  "bFlat",    "J", "6"),
  VC2: makeBox("VC2", "clarinet",  "bass",     "J", "6"),
});

export default function InventoryPage() {
  const [filterId, setFilterId] = React.useState();

  const { data, loading } = useQuery(QUERY);

  useSubscription(SUBSCRIPTION, {
    onSubscriptionData: ({ client, subscriptionData }) => {
      const data = client.cache.readQuery({ query: QUERY });
      const update = { ...data };

      const change = subscriptionData.data.inventoryChangeStream;
      switch (change.operationType) {
        case "insert":
        case "update":
          if (data.inventory.find(({ id }) => id === change.id)) return;
          update.inventory = [...data.inventory, change.inventory];
          break;

        case "delete":
          update.inventory = data.inventory.filter(
            ({ id }) => id !== change.id
          );
          break;

        default:
          return;
      }

      client.cache.writeQuery({
        query: QUERY,
        data: update,
      });
    },
  });

  if (loading) return <Layout.Loading padded />;

  const boxes = data?.inventory.reduce((acc, entry) => {
    acc[entry.box]?.entries.push(entry);
    return acc;
  }, makeBoxes());

  return (
    <Container fixed sx={{ p: 2 }}>
      <Layout.Toolbar fixed>
        <Form onSubmit={() => {}}>
          {() => (
            <>
              <SignatureMouthpieceSelectField
                name="signatureMouthpieceId"
                clearable
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 300 }}
              />

              <OnChange name="signatureMouthpieceId">{setFilterId}</OnChange>
            </>
          )}
        </Form>
      </Layout.Toolbar>

      <AddInventoryEntryForm>
        {({ open }) =>
          Object.values(boxes ?? {}).map((box) => (
            <InventoryBox
              key={box.id}
              box={box}
              onInsert={open}
              filterId={filterId}
            />
          ))
        }
      </AddInventoryEntryForm>
    </Container>
  );
}
